import { Navigate, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from './components/AppShell.js';
import { LoginScreen } from './screens/LoginScreen.js';
import { FloorScreen } from './screens/FloorScreen.js';
import { TimelineScreen } from './screens/TimelineScreen.js';
import { ReservationsScreen } from './screens/ReservationsScreen.js';
import { WaitlistScreen } from './screens/WaitlistScreen.js';
import { GuestsScreen } from './screens/GuestsScreen.js';
import { PreShiftScreen } from './screens/PreShiftScreen.js';
import { ReportsScreen } from './screens/ReportsScreen.js';
import { SettingsScreen } from './screens/SettingsScreen.js';
import { InboxScreen } from './screens/InboxScreen.js';
import { UsersScreen } from './screens/UsersScreen.js';
import { BranchScreen } from './screens/BranchScreen.js';
import { CorporateScreen } from './screens/CorporateScreen.js';
import { AcceptInviteScreen } from './screens/AcceptInviteScreen.js';
import { useSession } from './lib/session.js';

/**
 * Routes.
 *
 * Screens a role cannot use are not routed at all, rather than rendered
 * disabled. A host who can see a greyed-out Reports tab learns the feature
 * exists and asks for it; the server refuses either way, so hiding it is the
 * kinder half of the same answer.
 */
export function App() {
  return (
    <Routes>
      {/* Public by necessity: someone accepting an invitation has an account
          but no password yet, so there is nothing to authenticate with. It sits
          outside the session gate rather than inside it. */}
      <Route path="/invite/:token" element={<AcceptInviteScreen />} />
      <Route path="*" element={<AuthenticatedApp />} />
    </Routes>
  );
}

function AuthenticatedApp() {
  const { t } = useTranslation();
  const { user, loading, can, venueId } = useSession();

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-slate-500">{t('app.loading')}</p>
      </main>
    );
  }

  if (!user) return <LoginScreen />;

  if (!venueId) {
    return (
      <main className="flex min-h-screen items-center justify-center p-6">
        <p className="text-sm text-slate-500">{t('errors.forbidden')}</p>
      </main>
    );
  }

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<Navigate to="/floor" replace />} />
        <Route path="/floor" element={<FloorScreen />} />
        <Route path="/timeline" element={<TimelineScreen />} />
        <Route path="/reservations" element={<ReservationsScreen />} />
        <Route path="/waitlist" element={<WaitlistScreen />} />
        <Route path="/guests" element={<GuestsScreen />} />
        <Route path="/pre-shift" element={<PreShiftScreen />} />
        <Route path="/inbox" element={<InboxScreen />} />

        {can('report:read') ? (
          <Route path="/reports" element={<ReportsScreen />} />
        ) : null}

        <Route path="/settings" element={<SettingsScreen />} />
        {can('floor:configure') ? <Route path="/branch" element={<BranchScreen />} /> : null}
        {can('guest:read') ? <Route path="/corporate" element={<CorporateScreen />} /> : null}
        {can('user:manage') ? <Route path="/users" element={<UsersScreen />} /> : null}
        <Route path="*" element={<Navigate to="/floor" replace />} />
      </Route>
    </Routes>
  );
}
